import React, { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { useAppStore } from '@/hooks/useAppStore'
import { Topbar } from '@/components/shared/Topbar'
import { Atmosphere } from '@/components/shared/Atmosphere'
import { DNA_PROFILES, DESTINATIONS } from '@/data/destinations'
import { generatePassageDNASummary } from '@/services/ai'
import { getDestinationImages, type Img } from '@/services/images'
import { FallbackImage } from '@/components/shared/FallbackImage'
import { Attribution } from '@/components/shared/Attribution'
import { openDeal, NETWORK_LABELS } from '@/services/affiliates'
import type { Destination } from '@/types'

export function DnaScreen() {
  const { dnaKey, answers, setSelectedDestination, setScreen, aiSummary, setAiSummary } = useAppStore()
  const [selected, setSelected] = useState<Destination | null>(null)
  const [aiLoading, setAiLoading] = useState(false)
  const [imagesByDest, setImagesByDest] = useState<Record<string, Img[]>>({})
  const expandedRef = useRef<HTMLDivElement>(null)

  const profile = DNA_PROFILES[dnaKey]
  const dests = DESTINATIONS[dnaKey] ?? []

  useEffect(() => {
    if (dests.length > 0) setSelected(dests[0])
  }, [dnaKey])

  useEffect(() => {
    if (!profile || answers.length === 0 || aiSummary) return
    setAiLoading(true)
    const destinationNames = (DESTINATIONS[dnaKey] ?? []).map((d) => d.name)
    generatePassageDNASummary(profile.name, answers.map((a) => a.label), destinationNames)
      .then((s) => { setAiSummary(s); setAiLoading(false) })
      .catch(() => setAiLoading(false))
  }, [dnaKey])

  // Fetch imagery for card thumbnails + expanded gallery (4 images per destination).
  useEffect(() => {
    let cancelled = false
    dests.forEach(async (d) => {
      if (imagesByDest[d.id]) return
      const images = await getDestinationImages(d, 4)
      if (!cancelled) setImagesByDest((prev) => ({ ...prev, [d.id]: images }))
    })
    return () => { cancelled = true }
  }, [dnaKey])

  const pickDest = (d: Destination) => {
    setSelected(d)
    setTimeout(() => expandedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100)
  }

  const goJourney = () => { if (selected) { setSelectedDestination(selected); setScreen('journey') } }

  if (!profile) return null
  const desc = aiSummary || profile.description
  const selectedImages = selected ? (imagesByDest[selected.id] || []) : []
  // Split the profile title into words so we can italicise/gold-accent the middle word(s), e.g. "The <em>Grand Scholar</em>"
  const titleHtml = profile.title.replace('<em>', '<em style="color:var(--gold2);font-style:italic;">')

  return (
    <div style={{
      minHeight: '100vh',
      background: 'radial-gradient(ellipse at 50% 0%, #33200F 0%, #241209 55%, #180C05 100%)',
      position: 'relative',
    }}>
      <Atmosphere count={18} />
      <div style={{ position: 'relative', zIndex: 5 }}>
        <Topbar backTo="quiz" />
      </div>

      <div style={{ width: '100%', maxWidth: 760, margin: '0 auto' }}>
      {/* DNA Hero */}
      <div style={{ padding: '36px 22px 26px', borderBottom: '1px solid rgba(212,160,23,0.18)', position: 'relative', zIndex: 5 }}>
        <div style={{ display: 'inline-block', fontFamily: 'var(--serif-caps)', fontSize: 9, letterSpacing: '0.15em', color: 'var(--gold2)', border: '1px solid rgba(212,160,23,0.4)', padding: '4px 12px', marginBottom: 16, textTransform: 'uppercase' }}>
          ✦ Your Travel DNA
        </div>

        <motion.h1 initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          style={{ fontFamily: 'var(--serif-display)', fontSize: 'clamp(28px, 5vw, 44px)', color: '#F5EDD8', lineHeight: 1.18, marginBottom: 14 }}
          dangerouslySetInnerHTML={{ __html: titleHtml }} />

        {aiLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 48 }}>
            {[0, 1, 2].map((i) => (
              <motion.div key={i} style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--gold)' }}
                animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.4, repeat: Infinity, delay: i * 0.24 }} />
            ))}
            <span style={{ fontFamily: 'var(--serif-body)', fontSize: 13, color: 'rgba(245,237,216,0.6)', fontStyle: 'italic' }}>
              Passage is reading your journey…
            </span>
          </div>
        ) : (
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}
            style={{ fontFamily: 'var(--serif-body)', fontSize: 16, color: 'rgba(245,237,216,0.72)', lineHeight: 1.8, maxWidth: 520, marginBottom: 16 }}>
            {desc}
          </motion.p>
        )}

        {aiSummary && (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(212,160,23,0.08)', border: '1px solid rgba(212,160,23,0.25)', padding: '3px 10px', marginBottom: 10 }}>
            <span style={{ fontFamily: 'var(--serif-caps)', fontSize: 8, letterSpacing: '0.12em', color: 'var(--gold2)', textTransform: 'uppercase' }}>✦ Personalised by Claude AI</span>
          </div>
        )}

        {/* Answer chips */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {answers.map((a, i) => (
            <span key={i} style={{ fontFamily: 'var(--serif-body)', fontSize: 12, padding: '4px 10px', border: '1px solid rgba(212,160,23,0.2)', color: 'rgba(245,237,216,0.68)', background: 'rgba(255,255,255,0.03)' }}>
              {a.emoji} {a.label}
            </span>
          ))}
        </div>
      </div>

      {/* Destinations */}
      <div style={{ padding: '0 18px', position: 'relative', zIndex: 5 }}>
        <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 9, letterSpacing: '0.14em', color: 'rgba(245,237,216,0.5)', textTransform: 'uppercase', padding: '20px 0 12px' }}>
          Destinations curated to your lifetime of taste — select to explore
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 14 }}>
          {dests.map((d) => {
            const cardImg = (imagesByDest[d.id] || [])[0]
            return (
              <motion.button key={d.id} onClick={() => pickDest(d)}
                whileHover={{ y: -3 }} whileTap={{ scale: 0.98 }} transition={{ duration: 0.4 }}
                className={selected?.id === d.id ? 'p-glow-card' : ''}
                style={{
                  border: selected?.id === d.id ? '2px solid var(--gold)' : '1px solid rgba(212,160,23,0.18)',
                  background: 'rgba(255,255,255,0.03)', overflow: 'hidden', cursor: 'pointer', textAlign: 'left',
                }}>
                <div style={{ position: 'relative', height: 80 }}>
                  <FallbackImage
                    src={cardImg?.thumb || cardImg?.url}
                    alt={d.name}
                    style={{ height: 80 }}
                    fallback={
                      <div style={{ height: 80, background: d.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 34 }}>
                        {d.emoji}
                      </div>
                    }
                  />
                  <div style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(24,12,5,0.85)', color: 'var(--gold2)', fontFamily: 'var(--serif-caps)', fontSize: 8, padding: '2px 7px', letterSpacing: '0.06em', border: '1px solid rgba(212,160,23,0.3)' }}>
                    Save {d.saving}
                  </div>
                </div>
                <div style={{ padding: '9px 10px', borderTop: '1px solid rgba(212,160,23,0.15)' }}>
                  {d.country && (
                    <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 8, letterSpacing: '0.1em', color: 'rgba(245,237,216,0.5)', textTransform: 'uppercase', marginBottom: 2 }}>
                      {d.flag} {d.country}
                    </div>
                  )}
                  <div style={{ fontFamily: 'var(--serif-display)', fontSize: 15, color: '#F5EDD8' }}>{d.name}</div>
                  <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 9, letterSpacing: '0.08em', color: 'var(--gold2)', textTransform: 'uppercase', marginTop: 2 }}>{d.theme}</div>
                  <div style={{ fontFamily: 'var(--serif-body)', fontSize: 9.5, color: 'rgba(245,237,216,0.5)', marginTop: 3, fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {d.stops.slice(0, 3).map((s) => s.name).join(' · ')}
                  </div>
                </div>
              </motion.button>
            )
          })}
        </div>

        {/* Expanded */}
        {selected && (
          <motion.div ref={expandedRef} key={selected.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="p-glow-card"
            style={{ border: '1px solid rgba(212,160,23,0.22)', background: 'rgba(255,255,255,0.025)', overflow: 'hidden', marginBottom: 24 }}>

            {/* Photo hero (larger) — cinematic banner */}
            <div style={{ position: 'relative', aspectRatio: '22 / 10' }}>
              <FallbackImage
                src={selectedImages[0]?.url}
                alt={selected.name}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
                fallback={
                  <div style={{ position: 'absolute', inset: 0, background: selected.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 56 }}>
                    {selected.emoji}
                  </div>
                }
              />
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(24,12,5,0.15) 0%, transparent 35%, rgba(24,12,5,0.9) 100%)' }} />
              <div style={{ position: 'absolute', top: 12, right: 12, background: 'rgba(24,12,5,0.85)', color: 'var(--gold2)', fontFamily: 'var(--serif-caps)', fontSize: 10, padding: '4px 10px', border: '1px solid rgba(212,160,23,0.35)' }}>Save {selected.saving}</div>
              <div style={{ position: 'absolute', bottom: 14, left: 16, textShadow: '0 2px 10px rgba(0,0,0,0.7)' }}>
                <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 10, letterSpacing: '0.12em', color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase' }}>
                  {selected.flag} {selected.country ? `${selected.country} ›` : ''}
                </div>
                <div style={{ fontFamily: 'var(--serif-display)', fontSize: 26, color: '#fff', fontWeight: 600 }}>
                  <em style={{ color: 'var(--gold2)', fontStyle: 'italic' }}>{selected.name}</em>
                </div>
                <div style={{ fontFamily: 'var(--serif-body)', fontSize: 12, color: 'rgba(255,255,255,0.85)', marginTop: 2 }}>{selected.theme}</div>
                <div style={{ fontFamily: 'var(--serif-body)', fontSize: 11, color: 'rgba(232,196,104,0.9)', marginTop: 4, fontStyle: 'italic' }}>
                  {selected.stops.slice(0, 4).map((s) => s.name).join(' · ')}
                </div>
              </div>
            </div>

            {/* Destination imagery — 2x2 gallery */}
            {selectedImages.length > 0 && (
              <div style={{ padding: '18px 18px 4px' }}>
                <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 9, letterSpacing: '0.12em', color: 'rgba(245,237,216,0.5)', textTransform: 'uppercase', marginBottom: 8 }}>
                  Destination Imagery
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                  {selectedImages.slice(0, 4).map((img, i) => (
                    <FallbackImage key={i} src={img.thumb || img.url} alt={img.alt} style={{ aspectRatio: '4 / 3', width: '100%', border: '1px solid rgba(212,160,23,0.15)' }} fallback={<div style={{ aspectRatio: '4 / 3', width: '100%', background: selected.gradient }} />} />
                  ))}
                </div>
                {selectedImages[0] && (
                  <div style={{ marginTop: 6 }}><Attribution img={selectedImages[0]} dark /></div>
                )}
              </div>
            )}

            {/* Destination facts — 2x2 stat grid */}
            <div style={{ padding: '18px 18px 4px' }}>
              <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 9, letterSpacing: '0.12em', color: 'rgba(245,237,216,0.5)', textTransform: 'uppercase', marginBottom: 8 }}>
                Destination Facts
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {selected.facts.map((f, i) => (
                  <div key={i} style={{ border: '1px solid rgba(212,160,23,0.18)', padding: '10px 12px', background: 'rgba(255,255,255,0.03)' }}>
                    <div style={{ fontFamily: 'var(--serif-display)', fontSize: 19, color: 'var(--gold2)' }}>{f.value}</div>
                    <div style={{ fontFamily: 'var(--serif-body)', fontSize: 11, color: 'rgba(245,237,216,0.62)', marginTop: 2 }}>{f.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Why you'll love it */}
            <div style={{ padding: '18px 18px 4px' }}>
              <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 9, letterSpacing: '0.12em', color: 'rgba(245,237,216,0.5)', textTransform: 'uppercase', marginBottom: 8 }}>
                Why You'll Love It
              </div>
              <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {selected.loveIt.map((line, i) => (
                  <li key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontFamily: 'var(--serif-body)', fontSize: 13, color: 'rgba(245,237,216,0.78)', lineHeight: 1.65 }}>
                    <span style={{ color: 'var(--gold2)', flexShrink: 0, marginTop: 2 }}>✦</span>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Stats (existing, kept) */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', borderTop: '1px solid rgba(212,160,23,0.18)', borderBottom: '1px solid rgba(212,160,23,0.18)', marginTop: 18 }}>
              {Object.entries(selected.stats).map(([k, v]) => (
                <div key={k} style={{ padding: '12px 13px', borderRight: '1px solid rgba(212,160,23,0.15)' }}>
                  <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 8, letterSpacing: '0.1em', color: 'rgba(245,237,216,0.5)', textTransform: 'uppercase', marginBottom: 3 }}>{k}</div>
                  <div style={{ fontFamily: 'var(--serif-display)', fontSize: 18, color: '#F5EDD8' }}>{v}</div>
                </div>
              ))}
            </div>

            {/* Deals — restyled rows with network badge + save chip */}
            <div style={{ padding: '16px 18px 4px' }}>
              <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 9, letterSpacing: '0.12em', color: 'rgba(245,237,216,0.5)', textTransform: 'uppercase', marginBottom: 4 }}>
                ✦ Live Partner Offers — Unlocked For You
              </div>
            </div>
            <div style={{ marginBottom: 4 }}>
              {selected.deals.map((deal, i) => (
                <motion.button key={i} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.1, duration: 0.5 }}
                  onClick={() => openDeal(deal, selected.name)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                    padding: '11px 18px',
                    background: 'transparent', border: 'none', borderBottom: '1px solid rgba(212,160,23,0.15)',
                    cursor: 'pointer', textAlign: 'left',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(212,160,23,0.06)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <div style={{ width: 34, height: 34, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(212,160,23,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>{deal.icon}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: 'var(--serif-display)', fontSize: 13, color: '#F5EDD8' }}>{deal.title}</div>
                    <div style={{ fontFamily: 'var(--serif-body)', fontSize: 11, color: 'rgba(245,237,216,0.58)', marginTop: 1 }}>{deal.detail}</div>
                    <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 8, color: 'var(--gold2)', letterSpacing: '0.06em', marginTop: 2, textTransform: 'uppercase' }}>via {NETWORK_LABELS[deal.network] || deal.network}</div>
                  </div>
                  <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 9, padding: '3px 8px', border: '1px solid rgba(212,160,23,0.4)', color: 'var(--gold2)', whiteSpace: 'nowrap' }}>{deal.saving}</div>
                </motion.button>
              ))}
            </div>

            <button onClick={goJourney} className="p-glow-gold" style={{
              display: 'block', width: '100%', padding: 16,
              background: 'linear-gradient(90deg, var(--gold), var(--gold2))', color: '#241209',
              fontFamily: 'var(--serif-caps)', fontSize: 12, letterSpacing: '0.14em',
              textTransform: 'uppercase', border: 'none', cursor: 'pointer',
            }}>
              See my curated journey →
            </button>
          </motion.div>
        )}
      </div>
      </div>
    </div>
  )
}
